/**
 * History -> Runs (2026-10-22): the words for one automation run record (backend getErrorMessage `runs`, written by the
 * automation's lib/runHistory.js). Pure, so it is tested on its own (runHistoryView.test.js).
 */
import { shortLabel } from "./policyParts";
import { friendlyError } from "./friendlyError";

/** 252000 -> "4m 12s"; 45000 -> "45s"; 3720000 -> "1h 2m". */
export function durationText(ms) {
  if (ms === null || ms === undefined || Number.isNaN(Number(ms))) return "—";
  const s = Math.max(0, Math.round(Number(ms) / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${String(sec).padStart(2, "0")}s`;
  return `${sec}s`;
}

const TRIGGER = {
  created: "Created by",
  edited: "Edited and re-submitted by",
  retried: "Retried by",
  resubmitted: "Re-submitted by",
  released: "Released from hold by",
  "stage-run": "Step run again by",
  "tp-details": "Started after the TP policy details were entered by",
};

/** Who started the run, as a sentence. */
export function triggerText(trigger = {}) {
  const kind = trigger.kind || "queued";
  if (kind === "auto-retry") return "Automatic retry (after the last attempt failed)";
  if (kind === "queued") return "Queued";
  const lead = TRIGGER[kind] || "Started by";
  return `${lead} ${trigger.byName || trigger.by || "someone (not recorded)"}`;
}

/** The insurer login the run used - never the password (it is not stored). */
export function loginText(credential, company) {
  if (!credential || !credential.username) {
    return /brisk/i.test(String(company || "")) ? "No insurer login (Brisk certificate)" : "Not recorded";
  }
  const whose = credential.source === "client" ? "client's login" : credential.source === "user" ? "user's own login" : "login";
  return `${credential.username} · ${whose}${credential.ownerName ? ` (${credential.ownerName})` : ""}`;
}

const COMPANY = { reliance: "Reliance", kshema: "KSHEMA", national: "National", brisk: "Brisk" };
export const companyText = (c) => COMPANY[String(c || "").toLowerCase()] || (c ? String(c) : "");

/** The run's part and insurer: "OD + TP · Reliance". */
export const runTitle = (run = {}) => [shortLabel(run.part), companyText(run.company)].filter(Boolean).join(" · ");

/** { label, tone } of how the run ended. */
export function outcomeView(run = {}) {
  const o = String(run.outcome || "").toLowerCase();
  if (!o || o === "running") return { label: run.endedAt ? "Ended" : "Running", tone: "info" };
  if (o === "completed") return { label: "Completed", tone: "success" };
  if (o === "completed_with_errors") return { label: "Completed with warnings", tone: "warning" };
  if (o === "failed_post_submission") return { label: "Failed after the proposal was saved", tone: "error" };
  if (o.startsWith("failed")) return { label: "Failed", tone: "error" };
  if (o === "pending") return { label: "Failed - will be retried", tone: "warning" };
  if (o === "hold") return { label: "Stopped by Hold", tone: "warning" };
  return { label: o.charAt(0).toUpperCase() + o.slice(1).replace(/_/g, " "), tone: "default" };
}

/** The failure in plain words (the same wording as everywhere else), or "". */
export const runReason = (run = {}) => (run.error ? friendlyError(run.error, { stage: run.outcome === "failed_post_submission" ? "post-submission" : undefined }).title : "");

const STEP_ORDER = ["proposal", "kyc", "payment"];
const STEP_LABEL = { proposal: "Proposal", kyc: "KYC", payment: "Payment" };
const MODE = { wallet: "Wallet", link: "Link", qr: "QR" };

/**
 * The three steps of a run: [{ key, label, text, state }]
 *   state "done" | "failed" | "earlier" (finished by an earlier run - a later step of this run exists) | "none"
 */
export function stepRows(run = {}) {
  const steps = run.steps || {};
  const lastSeen = Math.max(-1, ...STEP_ORDER.map((k, i) => (steps[k] ? i : -1)));
  return STEP_ORDER.map((key, i) => {
    const s = steps[key];
    const label = key === "payment" && s && MODE[s.mode] ? `${STEP_LABEL[key]} (${MODE[s.mode]})` : STEP_LABEL[key];
    if (s) return { key, label, text: durationText(s.ms), state: s.status === "failed" ? "failed" : "done", at: s.at || null };
    if (i < lastSeen) return { key, label, text: "done earlier", state: "earlier" };
    return { key, label, text: "—", state: "none" };
  });
}
